/**
 * components/email/DeviceGuide.tsx — per-client setup walkthroughs for the
 * "Connect your devices" page. Every guide inlines the concrete values
 * (host, port, security, username) so a customer can follow it without
 * looking back at the settings card. Each guide ends with a
 * Troubleshooting disclosure.
 *
 * Pure presentation: no fetching. The page passes the resolved settings.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ConnectionSettings, securityLabel } from '../../config/mail';

export type GuideId =
  | 'iphone'
  | 'android'
  | 'outlook-win'
  | 'outlook-mac'
  | 'apple-mail'
  | 'thunderbird'
  | 'windows-mail'
  | 'gmail-web'
  | 'other';

export const GUIDES: Array<{ id: GuideId; label: string; short: string }> = [
  { id: 'iphone', label: 'iPhone & iPad', short: 'iOS' },
  { id: 'android', label: 'Android', short: 'Android' },
  { id: 'outlook-win', label: 'Outlook for Windows', short: 'Outlook Win' },
  { id: 'outlook-mac', label: 'Outlook for Mac', short: 'Outlook Mac' },
  { id: 'apple-mail', label: 'Apple Mail (macOS)', short: 'Apple Mail' },
  { id: 'thunderbird', label: 'Thunderbird', short: 'Thunderbird' },
  { id: 'windows-mail', label: 'Windows Mail / new Outlook', short: 'Windows Mail' },
  { id: 'gmail-web', label: 'Gmail on the web', short: 'Gmail' },
  { id: 'other', label: 'Other app', short: 'Other' },
];

export interface GuideContext {
  settings: ConnectionSettings;
  /** Selected mailbox address (or a placeholder like you@domain). */
  email: string;
  domain: string;
  /** Path to the mailboxes page (password reset link). */
  mailboxesPath: string;
  /** iOS profile download handler; undefined hides the button. */
  onDownloadProfile?: () => void;
  downloading?: boolean;
  downloadError?: string | null;
  /** True when a real mailbox is selected (enables the profile button). */
  hasMailbox: boolean;
  /** Print rendering: open every disclosure. */
  printAll?: boolean;
}

// ---------------------------------------------------------------------------
// Small building blocks

const V: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <code className="font-mono text-[0.95em] bg-secondary-50 border border-secondary-200 rounded px-1 py-0.5 text-primary-navy break-all">{children}</code>
);

const Steps: React.FC<{ children: React.ReactNode; start?: number }> = ({ children, start }) => (
  <ol start={start} className="list-decimal pl-6 space-y-2 text-sm text-primary-navy/90 marker:text-primary-navy marker:font-semibold">
    {children}
  </ol>
);

const Sub: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section className="space-y-2">
    <h4 className="text-sm font-semibold text-primary-navy">{title}</h4>
    {children}
  </section>
);

const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs text-primary-navy/80 bg-secondary-50 border border-secondary-200 rounded-lg p-3">{children}</p>
);

/** The IMAP / SMTP block of steps that most guides share. */
const ServerSteps: React.FC<{ ctx: GuideContext; incomingLabel?: string; outgoingLabel?: string }> = ({ ctx, incomingLabel = 'Incoming mail server (IMAP)', outgoingLabel = 'Outgoing mail server (SMTP)' }) => {
  const { settings: s, email } = ctx;
  return (
    <>
      <li>
        <strong>{incomingLabel}:</strong> host <V>{s.imap.host}</V>, port <V>{s.imap.port}</V>, security <V>{securityLabel(s.imap.security)}</V>,
        username <V>{email}</V>, password = the mailbox password.
      </li>
      <li>
        <strong>{outgoingLabel}:</strong> host <V>{s.smtp.host}</V>, port <V>{s.smtp.port}</V>, security <V>{securityLabel(s.smtp.security)}</V>,
        authentication <V>{s.smtp.auth}</V> with the <em>same</em> username <V>{email}</V> and password. Make sure
        "outgoing server requires authentication" is on.
      </li>
    </>
  );
};

export const Troubleshooting: React.FC<{ ctx: GuideContext; extra?: React.ReactNode; open?: boolean }> = ({ ctx, extra, open }) => {
  const { settings: s, domain, mailboxesPath } = ctx;
  const altPort = s.smtp.port === 465 ? 587 : 465;
  const altSec = s.smtp.port === 465 ? 'STARTTLS' : 'SSL/TLS';
  return (
    <details open={open ?? ctx.printAll} className="group border border-gray-200 rounded-lg bg-white">
      <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-primary-navy flex items-center gap-2">
        <span aria-hidden className="transition-transform group-open:rotate-90">▸</span> Troubleshooting
      </summary>
      <div className="px-4 pb-4 space-y-3 text-sm text-primary-navy/90">
        <div>
          <strong>"Wrong password" / "Authentication failed".</strong> The username is the <em>full</em> address
          (<V>{ctx.email}</V>), not just the part before the @. The password is the mailbox password set in{' '}
          <Link to={mailboxesPath} className="underline">Mailboxes</Link>, not your 12and22 account password.
          If you have just changed it, wait a minute and retry; some apps cache the old one.
        </div>
        <div>
          <strong>465 vs 587.</strong> We publish SMTP on port <V>{s.smtp.port}</V> with <V>{securityLabel(s.smtp.security)}</V>.
          If your app only offers the other option, try port <V>{altPort}</V> with <V>{altSec}</V> — port 465 always
          pairs with SSL/TLS and 587 with STARTTLS. Never use "None" for security.
        </div>
        <div>
          <strong>Certificate or identity warnings.</strong> Our certificate is issued for <V>{s.imap.host}</V>.
          If your app warns that the name doesn't match, you probably typed <V>mail.{domain}</V> or <V>{domain}</V> as
          the server — change it to <V>{s.imap.host}</V> exactly. Do not tick "accept untrusted certificates".
        </div>
        <div>
          <strong>Mail arrives but sending fails.</strong> Check that the outgoing server uses authentication with
          the same username/password, and that you are under the sending limit of{' '}
          {s.limits.outbound_per_hour} messages per hour and {s.limits.max_message_mb} MB per message.
        </div>
        <div>
          <strong>Forgot the password?</strong> Open{' '}
          <Link to={mailboxesPath} className="underline">Mailboxes</Link>, choose <em>Edit</em> next to the address and set a
          new password — then update it in every device that uses the mailbox.
        </div>
        {extra}
      </div>
    </details>
  );
};

// ---------------------------------------------------------------------------
// Guides

const IphoneGuide: React.FC<{ ctx: GuideContext }> = ({ ctx }) => {
  const { settings: s, email } = ctx;
  return (
    <div className="space-y-5">
      <Sub title="Automatic — install a profile (recommended)">
        <p className="text-sm text-primary-navy/90">
          The profile fills in every server setting for <V>{email}</V>; you only type the password.
        </p>
        <div className="flex flex-wrap items-center gap-3 print:hidden">
          <button
            type="button"
            onClick={ctx.onDownloadProfile}
            disabled={!ctx.hasMailbox || !ctx.onDownloadProfile || ctx.downloading}
            className="text-sm bg-secondary-500 text-primary-navy font-medium px-4 py-2 rounded-lg hover:bg-secondary-600 disabled:opacity-50"
          >
            {ctx.downloading ? 'Preparing…' : 'Download profile (.mobileconfig)'}
          </button>
          {!ctx.hasMailbox && <span className="text-xs text-gray-500">Select a mailbox first.</span>}
          {ctx.downloadError && <span role="alert" className="text-xs text-primary-navy">🚨 {ctx.downloadError}</span>}
        </div>
        <Steps>
          <li>Open this page in Safari on the iPhone or iPad and tap <strong>Download profile</strong>. Allow the download when asked.</li>
          <li>Open <strong>Settings</strong>. A <strong>Profile Downloaded</strong> entry appears near the top — tap it (or go to General → VPN &amp; Device Management).</li>
          <li>Tap <strong>Install</strong>, enter your device passcode, then enter the mailbox password for <V>{email}</V> when prompted.</li>
          <li>Open <strong>Mail</strong>. The account is ready; the first sync can take a minute.</li>
        </Steps>
      </Sub>
      <Sub title="Manual setup">
        <Steps>
          <li>Open <strong>Settings → Mail → Accounts → Add Account → Other → Add Mail Account</strong>.</li>
          <li>Enter your name, the email <V>{email}</V>, the mailbox password and a description, then tap <strong>Next</strong>.</li>
          <li>Choose <strong>IMAP</strong> at the top.</li>
          <ServerSteps ctx={ctx} incomingLabel="Incoming Mail Server" outgoingLabel="Outgoing Mail Server" />
          <li>Tap <strong>Next</strong>, keep <strong>Mail</strong> on, then <strong>Save</strong>.</li>
          <li>
            If iOS fails to verify: go back into the account → <strong>Account → SMTP → {s.smtp.host}</strong>, switch{' '}
            <strong>Use SSL</strong> on and set <strong>Server Port</strong> to <V>{s.smtp.port}</V>. Then <strong>Advanced</strong> → Use SSL on, port <V>{s.imap.port}</V>.
          </li>
        </Steps>
      </Sub>
      <Troubleshooting ctx={ctx} extra={
        <div><strong>Profile won't install.</strong> Profiles must be downloaded in Safari, not Chrome or the Gmail app. Remove any older 12and22 profile first in Settings → General → VPN &amp; Device Management.</div>
      } />
    </div>
  );
};

const AndroidGuide: React.FC<{ ctx: GuideContext }> = ({ ctx }) => {
  const { settings: s, email } = ctx;
  return (
    <div className="space-y-5">
      <Sub title="Gmail app">
        <Steps>
          <li>Open <strong>Gmail → profile picture → Add another account → Other</strong>.</li>
          <li>Enter <V>{email}</V> and tap <strong>Set up manually</strong>, then choose <strong>Personal (IMAP)</strong>.</li>
          <li>Enter the mailbox password.</li>
          <li>
            <strong>Incoming server settings:</strong> username <V>{email}</V>, server <V>{s.imap.host}</V>, port <V>{s.imap.port}</V>,
            security type <V>{securityLabel(s.imap.security)}</V>.
          </li>
          <li>
            <strong>Outgoing server settings:</strong> require sign-in on, username <V>{email}</V>, SMTP server <V>{s.smtp.host}</V>,
            port <V>{s.smtp.port}</V>, security type <V>{securityLabel(s.smtp.security)}</V>.
          </li>
          <li>Pick sync frequency, enter your display name, tap <strong>Next</strong>.</li>
        </Steps>
      </Sub>
      <Sub title="Samsung Email">
        <Steps>
          <li>Open <strong>Email → Settings (gear) → Add account → Other</strong>.</li>
          <li>Enter <V>{email}</V> and the password, then tap <strong>Manual setup → IMAP account</strong>.</li>
          <ServerSteps ctx={ctx} incomingLabel="Incoming server" outgoingLabel="Outgoing server" />
          <li>Tap <strong>Sign in</strong>. Enable "Require authentication to send emails" if it isn't already.</li>
        </Steps>
      </Sub>
      <Sub title="Outlook mobile">
        <Steps>
          <li>Open <strong>Outlook → Settings → Add Mail Account → Add Email Account</strong>.</li>
          <li>Enter <V>{email}</V>, tap <strong>Continue</strong>, then choose <strong>IMAP</strong> (tap the ? icon → "Change account provider" if it isn't offered).</li>
          <li>Turn on <strong>Advanced settings</strong>.</li>
          <ServerSteps ctx={ctx} incomingLabel="IMAP Incoming Mail Server" outgoingLabel="SMTP Outgoing Mail Server" />
          <li>Tap the tick to sign in.</li>
        </Steps>
      </Sub>
      <Troubleshooting ctx={ctx} />
    </div>
  );
};

const OutlookWinGuide: React.FC<{ ctx: GuideContext }> = ({ ctx }) => {
  const { settings: s, email } = ctx;
  const auto = s.autoconfig.status.autodiscover || s.autoconfig.status.srv;
  return (
    <div className="space-y-5">
      {auto && (
        <Note>
          Autodiscover is set up for your domain, so Outlook should find the servers by itself: add the account with
          just <V>{email}</V> and the password. The manual steps below are the fallback.
        </Note>
      )}
      <Sub title="New Outlook (Windows 11 / Microsoft 365)">
        <Steps>
          <li>Open <strong>Settings (gear) → Accounts → Email accounts → Add account</strong>.</li>
          <li>Enter <V>{email}</V> and tap <strong>Continue</strong>.</li>
          <li>If Outlook asks for a provider, choose <strong>IMAP</strong> and expand <strong>Show more</strong>.</li>
          <li>
            IMAP incoming: server <V>{s.imap.host}</V>, port <V>{s.imap.port}</V>, secure connection <V>{securityLabel(s.imap.security)}</V>,
            username <V>{email}</V>, mailbox password.
          </li>
          <li>
            SMTP outgoing: server <V>{s.smtp.host}</V>, port <V>{s.smtp.port}</V>, secure connection <V>{securityLabel(s.smtp.security)}</V>,
            username <V>{email}</V>, same password.
          </li>
          <li>Tap <strong>Continue</strong>, then <strong>Done</strong>.</li>
        </Steps>
      </Sub>
      <Sub title="Classic Outlook (2016 / 2019 / 2021 / Microsoft 365 desktop)">
        <Steps>
          <li>Open <strong>File → Add Account</strong>.</li>
          <li>Enter <V>{email}</V>, open <strong>Advanced options</strong>, tick <strong>Let me set up my account manually</strong>, then <strong>Connect</strong>.</li>
          <li>Choose <strong>IMAP</strong>.</li>
          <li>
            Incoming mail: server <V>{s.imap.host}</V>, port <V>{s.imap.port}</V>, encryption method <V>{securityLabel(s.imap.security)}</V>.
          </li>
          <li>
            Outgoing mail: server <V>{s.smtp.host}</V>, port <V>{s.smtp.port}</V>, encryption method <V>{securityLabel(s.smtp.security)}</V>.
          </li>
          <li>Tap <strong>Next</strong>, enter the mailbox password, then <strong>Connect</strong>.</li>
          <li>
            If sending fails later: <strong>File → Account Settings → Account Settings… → double-click the account → More Settings → Outgoing Server</strong>,
            tick "My outgoing server (SMTP) requires authentication" and "Use same settings as my incoming mail server".
          </li>
        </Steps>
      </Sub>
      <Troubleshooting ctx={ctx} extra={
        <div><strong>Outlook keeps looking for Exchange.</strong> Cancel the automatic search and use the manual IMAP path above; we are an IMAP/SMTP service, not Exchange.</div>
      } />
    </div>
  );
};

const OutlookMacGuide: React.FC<{ ctx: GuideContext }> = ({ ctx }) => {
  const { settings: s, email } = ctx;
  return (
    <div className="space-y-5">
      <Steps>
        <li>Open <strong>Outlook → Settings (⌘,) → Accounts → + → New Account</strong> (or Tools → Accounts).</li>
        <li>Enter <V>{email}</V> and click <strong>Continue</strong>. If Outlook asks, choose <strong>IMAP/POP</strong>.</li>
        <li>Set the type to <strong>IMAP</strong>.</li>
        <li>
          Incoming: username <V>{email}</V>, password = mailbox password, server <V>{s.imap.host}</V>, port <V>{s.imap.port}</V>,
          tick <strong>Use SSL to connect</strong> ({securityLabel(s.imap.security)}).
        </li>
        <li>
          Outgoing: server <V>{s.smtp.host}</V>, port <V>{s.smtp.port}</V>, tick <strong>Use SSL to connect</strong> ({securityLabel(s.smtp.security)}).
          Outgoing authentication = <strong>Use incoming server info</strong>.
        </li>
        <li>Click <strong>Add Account</strong>, then <strong>Done</strong>.</li>
      </Steps>
      <Troubleshooting ctx={ctx} />
    </div>
  );
};

const AppleMailGuide: React.FC<{ ctx: GuideContext }> = ({ ctx }) => {
  const { settings: s, email } = ctx;
  return (
    <div className="space-y-5">
      <Steps>
        <li>Open <strong>Mail → Settings (⌘,) → Accounts → +</strong> (or System Settings → Internet Accounts → Add Account).</li>
        <li>Choose <strong>Other Mail Account…</strong> and click <strong>Continue</strong>.</li>
        <li>Enter your name, <V>{email}</V> and the mailbox password, then <strong>Sign In</strong>. Mail will say it couldn't verify — that just opens the server fields.</li>
        <li>Account type <strong>IMAP</strong>. Incoming mail server <V>{s.imap.host}</V>, outgoing mail server <V>{s.smtp.host}</V>, username <V>{email}</V>.</li>
        <li>Click <strong>Sign In</strong>, keep <strong>Mail</strong> ticked, click <strong>Done</strong>.</li>
        <li>
          Verify ports: <strong>Mail → Settings → Accounts → your account → Server Settings</strong>. Untick "Automatically manage connection settings"
          and set incoming port <V>{s.imap.port}</V> with <strong>Use TLS/SSL</strong> on, outgoing port <V>{s.smtp.port}</V> with
          <strong> Use TLS/SSL</strong> on and authentication <V>Password</V>.
        </li>
      </Steps>
      <Troubleshooting ctx={ctx} />
    </div>
  );
};

const ThunderbirdGuide: React.FC<{ ctx: GuideContext }> = ({ ctx }) => {
  const { settings: s, email } = ctx;
  const auto = s.autoconfig.status.autoconfig;
  return (
    <div className="space-y-5">
      {auto ? (
        <Note>
          <strong>Automatic setup works for your domain.</strong> Thunderbird reads our autoconfig record, so you only need
          your name, <V>{email}</V> and the password — it fills in every server field itself. The manual steps are a fallback.
        </Note>
      ) : (
        <Note>
          The autoconfig DNS record for your domain isn't detected yet, so Thunderbird will ask you for the servers.
          Add the optional <V>autoconfig</V> record on the setup page to make this automatic.
        </Note>
      )}
      <Steps>
        <li>Open <strong>Account Settings → Account Actions → Add Mail Account</strong> (or the Hamburger menu → New → Existing Mail Account).</li>
        <li>Enter your name, <V>{email}</V> and the mailbox password.</li>
        <li>
          {auto ? <>Click <strong>Continue</strong>. Thunderbird shows the detected settings — confirm IMAP and click <strong>Done</strong>.</>
               : <>Click <strong>Configure manually</strong>.</>}
        </li>
        <li>
          Incoming: protocol <V>IMAP</V>, hostname <V>{s.imap.host}</V>, port <V>{s.imap.port}</V>, connection security <V>{securityLabel(s.imap.security)}</V>,
          authentication <V>Normal password</V>, username <V>{email}</V>.
        </li>
        <li>
          Outgoing: hostname <V>{s.smtp.host}</V>, port <V>{s.smtp.port}</V>, connection security <V>{securityLabel(s.smtp.security)}</V>,
          authentication <V>Normal password</V>, username <V>{email}</V>.
        </li>
        <li>Click <strong>Re-test</strong>, then <strong>Done</strong>.</li>
      </Steps>
      <Troubleshooting ctx={ctx} />
    </div>
  );
};

const WindowsMailGuide: React.FC<{ ctx: GuideContext }> = ({ ctx }) => {
  const { settings: s, email } = ctx;
  return (
    <div className="space-y-5">
      <Note>
        Windows Mail has been replaced by the <strong>new Outlook</strong> on Windows 11. The steps are the same in both; the new
        Outlook steps are also under <em>Outlook for Windows</em>.
      </Note>
      <Steps>
        <li>Open <strong>Settings (gear) → Manage accounts → Add account</strong> (new Outlook: Settings → Accounts → Email accounts → Add account).</li>
        <li>Choose <strong>Advanced setup → Internet email</strong> (new Outlook: enter <V>{email}</V>, Continue, choose IMAP, expand "Show more").</li>
        <li>Email address and username: <V>{email}</V>. Password: the mailbox password. Account name and "send messages using this name": your choice.</li>
        <li>Incoming email server: <V>{s.imap.host}:{s.imap.port}</V>. Account type: <strong>IMAP4</strong>.</li>
        <li>Outgoing (SMTP) email server: <V>{s.smtp.host}:{s.smtp.port}</V>.</li>
        <li>Keep ticked: outgoing server requires authentication; use the same username and password for sending; require SSL for incoming; require SSL for outgoing.</li>
        <li>Click <strong>Sign in</strong>.</li>
      </Steps>
      <Troubleshooting ctx={ctx} />
    </div>
  );
};

const GmailWebGuide: React.FC<{ ctx: GuideContext }> = ({ ctx }) => {
  const { settings: s, email } = ctx;
  const smtpSsl = s.smtp.port === 465;
  return (
    <div className="space-y-5">
      <p className="text-sm text-primary-navy/90">
        Gmail can send <em>as</em> <V>{email}</V> and fetch its mail into your Google inbox. Set up both parts; the order doesn't matter.
      </p>
      <Sub title="Part 1 — Send mail as">
        <Steps>
          <li>In Gmail open <strong>Settings (gear) → See all settings → Accounts and Import</strong>.</li>
          <li>Next to <strong>Send mail as</strong>, click <strong>Add another email address</strong>.</li>
          <li>Name: your name. Email address: <V>{email}</V>. Untick <strong>Treat as an alias</strong> unless you want replies to land in Gmail's own thread. Click <strong>Next Step</strong>.</li>
          <li>
            SMTP Server: <V>{s.smtp.host}</V>. Port: <V>{s.smtp.port}</V>. Username: <V>{email}</V>. Password: the mailbox password.
            Select <strong>{smtpSsl ? 'Secured connection using SSL' : 'Secured connection using TLS'}</strong> ({securityLabel(s.smtp.security)}).
          </li>
          <li>Click <strong>Add Account</strong>. Google sends a confirmation code to <V>{email}</V> — read it in <a href={s.webmail_url} target="_blank" rel="noopener noreferrer" className="underline">webmail</a> and enter it.</li>
        </Steps>
      </Sub>
      <Sub title="Part 2 — Check mail from other accounts">
        <Steps>
          <li>Still under <strong>Accounts and Import</strong>, next to <strong>Check mail from other accounts</strong> click <strong>Add a mail account</strong>.</li>
          <li>Email address: <V>{email}</V>. Click <strong>Next</strong>, choose <strong>Import emails from my other account (POP3)</strong>, then <strong>Next</strong>.</li>
          <li>
            Username: <V>{email}</V>. Password: the mailbox password. POP Server: <V>{s.pop3.host}</V>. Port: <V>{s.pop3.port}</V>.
          </li>
          <li>Tick <strong>Always use a secure connection (SSL) when retrieving mail</strong> ({securityLabel(s.pop3.security)}). Tick <strong>Leave a copy of retrieved message on the server</strong> if you also read this mailbox elsewhere.</li>
          <li>Optionally label incoming messages, then click <strong>Add Account</strong>.</li>
        </Steps>
      </Sub>
      <Note>
        Gmail fetches by POP3 roughly once an hour, so new mail can lag. For instant delivery, read the mailbox in a proper mail app or webmail instead.
      </Note>
      <Troubleshooting ctx={ctx} extra={
        <div><strong>"Server returned error: Connection refused" in Gmail.</strong> Google only accepts ports 465 (SSL) or 587 (TLS) for sending and 995 (SSL) for POP fetching. Use exactly <V>{s.smtp.port}</V> and <V>{s.pop3.port}</V> as shown.</div>
      } />
    </div>
  );
};

const OtherGuide: React.FC<{ ctx: GuideContext }> = ({ ctx }) => {
  const { settings: s, email } = ctx;
  return (
    <div className="space-y-5">
      <p className="text-sm text-primary-navy/90">
        Any app that speaks IMAP (or POP3) and SMTP works. Look for "Add account → Other / IMAP / Manual setup" and enter:
      </p>
      <dl className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-x-4 gap-y-2 text-sm text-primary-navy/90">
        <dt className="text-gray-500">Account type</dt><dd><V>IMAP</V> (use POP3 only if you want mail downloaded and removed from the server)</dd>
        <dt className="text-gray-500">Email / username</dt><dd><V>{email}</V></dd>
        <dt className="text-gray-500">Password</dt><dd>the mailbox password</dd>
        <dt className="text-gray-500">Incoming (IMAP)</dt><dd><V>{s.imap.host}</V> port <V>{s.imap.port}</V>, {securityLabel(s.imap.security)}</dd>
        <dt className="text-gray-500">Incoming (POP3)</dt><dd><V>{s.pop3.host}</V> port <V>{s.pop3.port}</V>, {securityLabel(s.pop3.security)}</dd>
        <dt className="text-gray-500">Outgoing (SMTP)</dt><dd><V>{s.smtp.host}</V> port <V>{s.smtp.port}</V>, {securityLabel(s.smtp.security)}, authentication <V>{s.smtp.auth}</V></dd>
        <dt className="text-gray-500">Webmail</dt><dd><a href={s.webmail_url} target="_blank" rel="noopener noreferrer" className="underline break-all">{s.webmail_url}</a></dd>
      </dl>
      <Note>
        Sending limits: {s.limits.outbound_per_hour} messages per hour per mailbox and {s.limits.max_message_mb} MB per message
        (about {s.limits.attachment_mb} MB of attachments once encoded).
      </Note>
      <Troubleshooting ctx={ctx} />
    </div>
  );
};

export const DeviceGuide: React.FC<{ guide: GuideId; ctx: GuideContext }> = ({ guide, ctx }) => {
  switch (guide) {
    case 'iphone': return <IphoneGuide ctx={ctx} />;
    case 'android': return <AndroidGuide ctx={ctx} />;
    case 'outlook-win': return <OutlookWinGuide ctx={ctx} />;
    case 'outlook-mac': return <OutlookMacGuide ctx={ctx} />;
    case 'apple-mail': return <AppleMailGuide ctx={ctx} />;
    case 'thunderbird': return <ThunderbirdGuide ctx={ctx} />;
    case 'windows-mail': return <WindowsMailGuide ctx={ctx} />;
    case 'gmail-web': return <GmailWebGuide ctx={ctx} />;
    default: return <OtherGuide ctx={ctx} />;
  }
};

export default DeviceGuide;
